import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useFeatureFlag } from '@ibk/shared-feature-flags';
import { TranslationProvider, useTranslation } from '@ibk/shared-translation';
import { FeatureFlagUseCase } from '@ibk/shared-types';
import { Alert, BenefitCards, ButtonOptions, Card, Icon, Loading, WelcomeModal } from '@ibk/shared-ui';
import { BenefitsOnboardingWizard, HappyHolidaysEndOfTheYearRechargeModal, HomeSidebarBanners } from '../components';
import { AddressListModal } from '../components/AddressListModal';
import { ConfigurationPanel } from '../components/ConfigurationPanel';
import { ContractedBenefitsModal } from '../components/ContractedBenefitsModal';
import { ProductServiceSurveyAlert } from '../components/ProductServiceSurveyAlert';
import { QuickActions } from '../components/QuickActions';
import { RechargeDetailsModal } from '../components/RechargeDetailsModal';
import { RechargeHistoryContainer } from '../components/RechargeHistoryContainer';
import { TrackingCardsModal } from '../components/TrackingCardsModal';
import { useActiveContractProducts } from '../hooks/useActiveContractProducts';
import { useHappyHolidaysEndOfTheYearRechargeModal } from '../hooks/useHappyHolidaysEndOfTheYearRechargeModal';
import { useHasRecharges } from '../hooks/useHasRecharges';
import { useHomePageBenefitsWelcome } from '../hooks/useHomePageBenefitsWelcome';
import { beneficiosTranslations } from '../translations';
import { getActiveContractProducts } from '../utils/voucherUtils';
import { BeneficiariesPage } from './BeneficiariesPage';
import styles from './HomePage.module.css';
import { NetworkPage } from './NetworkPage';
export var HomePageTab;
(function (HomePageTab) {
    HomePageTab["RECHARGES"] = "recharges";
})(HomePageTab || (HomePageTab = {}));
function HomePageContent({ contractData, onNavigateToRecharge, appDomain }) {
    const [activeTab, setActiveTab] = useState(HomePageTab.RECHARGES);
    const [selectedRecharge, setSelectedRecharge] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isBeneficiariesModalOpen, setIsBeneficiariesModalOpen] = useState(false);
    const [isContractedBenefitsModalOpen, setIsContractedBenefitsModalOpen] = useState(false);
    const [isTrackingCardsModalOpen, setIsTrackingCardsModalOpen] = useState(false);
    const [isNetworkModalOpen, setIsNetworkModalOpen] = useState(false);
    const [isAddressListModalOpen, setIsAddressListModalOpen] = useState(false);
    const [isConfigurationPanelOpen, setIsConfigurationPanelOpen] = useState(false);
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const [repeatRechargeErrorVisible, setRepeatRechargeErrorVisible] = useState(false);
    useEffect(() => {
        const state = location.state;
        if (!state) {
            return;
        }
        if (state.fromHiring) {
            setIsContractedBenefitsModalOpen(true);
        }
        if (state.showRepeatRechargeError) {
            setRepeatRechargeErrorVisible(true);
            const nextState = { ...state };
            delete nextState.showRepeatRechargeError;
            const cleaned = Object.keys(nextState).length > 0 ? nextState : {};
            navigate(location.pathname, { replace: true, state: cleaned });
        }
    }, [location.pathname, location.state, navigate]);
    const { isFeatureFlagWhiteListed } = useFeatureFlag();
    const isMultipleAddressesEnabled = isFeatureFlagWhiteListed(FeatureFlagUseCase.BENEFICIOS_MULTI_ADDRESSES);
    const isRepeatRechargeEnabled = isFeatureFlagWhiteListed(FeatureFlagUseCase.BENEFICIOS_ENABLE_REPEAT_RECHARGE);
    const isBenefitsOnboardingEnabled = isFeatureFlagWhiteListed(FeatureFlagUseCase.BENEFICIOS_ENABLE_ONBOARDING);
    const onLegacyRecharge = useCallback(() => {
        if (onNavigateToRecharge) {
            onNavigateToRecharge();
        }
        else {
            navigate(`/beneficios/nova-recarga`);
        }
    }, [onNavigateToRecharge, navigate]);
    const { welcomeModalProps, benefitsOnboardingWizard } = useHomePageBenefitsWelcome({
        contract: contractData,
        isOnboardingFeatureEnabled: isBenefitsOnboardingEnabled,
        isOnboardingEligible: true,
        onLegacyPrimaryAction: onLegacyRecharge,
    });
    const { isModalCanBeOpened } = useHappyHolidaysEndOfTheYearRechargeModal();
    const handleConfigure = (type) => {
        navigate(`/beneficios/configurar/${type}`);
    };
    const handleNewRecharge = useCallback(() => {
        navigate(`/beneficios/nova-recarga`);
    }, [navigate]);
    const handleRepeatOrder = useCallback(() => {
        navigate(`/beneficios/nova-recarga?repeat=last`);
    }, [navigate]);
    const handleRechargeClick = (recharge) => {
        setSelectedRecharge(recharge);
        setIsModalOpen(true);
    };
    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedRecharge(null);
    };
    const handleOpenBeneficiariesModal = useCallback(() => {
        setIsBeneficiariesModalOpen(true);
    }, []);
    const handleOpenContractedBenefitsModal = useCallback(() => {
        setIsContractedBenefitsModalOpen(true);
    }, []);
    const handleCloseBeneficiariesModal = () => {
        setIsBeneficiariesModalOpen(false);
    };
    const handleCloseContractedBenefitsModal = useCallback(() => {
        setIsContractedBenefitsModalOpen(false);
        navigate('/beneficios', { replace: true, state: {} });
    }, [navigate]);
    const handleContractNewBenefit = useCallback(() => {
        setIsContractedBenefitsModalOpen(false);
        navigate('/beneficios/contratar');
    }, [navigate]);
    const handleOpenTrackingCardsModal = useCallback(() => {
        setIsTrackingCardsModalOpen(true);
    }, []);
    const handleCloseTrackingCardsModal = () => {
        setIsTrackingCardsModalOpen(false);
    };
    const handleOpenNetworkModal = useCallback(() => {
        setIsNetworkModalOpen(true);
    }, []);
    const handleCloseNetworkModal = () => {
        setIsNetworkModalOpen(false);
    };
    const handleNavigateToNetwork = useCallback(() => {
        handleOpenNetworkModal();
    }, [handleOpenNetworkModal]);
    const [isHappyHolidaysEndOfTheYearRechargeModalOpen, setIsHappyHolidaysEndOfTheYearRechargeModalOpen] = useState(false);
    const handleHappyHolidaysEndOfTheYearRechargeBannerClick = useCallback(() => {
        setIsHappyHolidaysEndOfTheYearRechargeModalOpen(true);
    }, []);
    const handleCloseHappyHolidaysEndOfTheYearRechargeModal = useCallback(() => {
        setIsHappyHolidaysEndOfTheYearRechargeModalOpen(false);
    }, []);
    const handleOpenAddressListModal = useCallback(() => {
        setIsAddressListModalOpen(true);
    }, []);
    const handleCloseAddressListModal = () => {
        setIsAddressListModalOpen(false);
    };
    const handleOpenConfigurationPanel = useCallback(() => {
        setIsConfigurationPanelOpen(true);
    }, []);
    const handleCloseConfigurationPanel = useCallback(() => {
        setIsConfigurationPanelOpen(false);
    }, []);
    useEffect(() => {
        if (isModalCanBeOpened) {
            const timeoutId = setTimeout(() => {
                setIsHappyHolidaysEndOfTheYearRechargeModalOpen(true);
            }, 2000);
            return () => clearTimeout(timeoutId);
        }
    }, [isModalCanBeOpened]);
    const { products, isLoading } = useActiveContractProducts({ voucherContractId: contractData.id });
    const { hasRecharges, isLoading: isLoadingHasRecharges } = useHasRecharges({
        contractId: contractData.id,
        enabled: isRepeatRechargeEnabled,
    });
    const isNewRechargeButtonLoading = isRepeatRechargeEnabled && isLoadingHasRecharges;
    const showNewRechargeOptions = isRepeatRechargeEnabled && hasRecharges === true;
    const newRechargeActions = useMemo(() => [
        {
            label: t('benefits.home.products.popover.repeat-order'),
            icon: 'arrowRedo',
            onClick: handleRepeatOrder,
            testIdIdentifier: 'RepeatOrder',
        },
        {
            label: t('benefits.home.products.popover.new-recharge'),
            icon: 'actionAddThin',
            onClick: handleNewRecharge,
            testIdIdentifier: 'NewRecharge',
        },
    ], [t, handleNewRecharge, handleRepeatOrder]);
    const newRechargeButton = useMemo(() => {
        if (isNewRechargeButtonLoading)
            return null;
        if (showNewRechargeOptions) {
            return (_jsx("span", { "data-benefits-onboarding-anchor": 'new-recharge', className: styles.newRechargeOnboardingTarget, children: _jsxs(ButtonOptions, { actions: newRechargeActions, testIdIdentifier: 'BenefitsNewRechargeOptions', triggerVariant: 'primary', children: [_jsx(Icon, { name: 'recharge', className: styles.icon }), t('benefits.home.products.new-recharge'), _jsx(Icon, { name: 'arrowChevronDown', className: styles.iconChevron, "aria-hidden": true })] }) }));
        }
        return (_jsx("span", { "data-benefits-onboarding-anchor": 'new-recharge', className: styles.newRechargeOnboardingTarget, children: _jsxs("button", { onClick: handleNewRecharge, "data-testid": 'benefits-button-home-new-recharge', className: styles.newRechargeButton, type: 'button', children: [_jsx(Icon, { name: 'recharge', className: styles.icon }), t('benefits.home.products.new-recharge')] }) }));
    }, [isNewRechargeButtonLoading, showNewRechargeOptions, newRechargeActions, t, handleNewRecharge]);
    const quickActions = useMemo(() => [
        {
            id: 'contractedBenefits',
            title: t('benefits.quick-actions.contracted-benefits.title'),
            icon: 'fileContract',
            onClick: handleOpenContractedBenefitsModal,
            'data-testid': 'quick-action-contracted-benefits',
        },
        {
            id: 'beneficiaries',
            title: t('benefits.quick-actions.beneficiaries.title'),
            icon: 'user',
            onClick: handleOpenBeneficiariesModal,
            'data-testid': 'quick-action-beneficiaries',
        },
        {
            id: 'trackingCards',
            title: t('benefits.quick-actions.tracking-cards.title'),
            icon: 'cardOrange',
            onClick: handleOpenTrackingCardsModal,
            'data-testid': 'quick-action-tracking-cards',
        },
        {
            id: 'addressList',
            title: t('deliveryAddress'),
            icon: 'location',
            onClick: handleOpenAddressListModal,
            'data-testid': 'quick-action-address-list',
            enabled: isMultipleAddressesEnabled,
        },
        {
            id: 'network',
            title: t('benefits.network.title'),
            icon: 'store',
            onClick: handleNavigateToNetwork,
            'data-testid': 'quick-action-network',
        },
        {
            id: 'configuration',
            title: t('benefits.quick-actions.configuration.title'),
            icon: 'config',
            onClick: handleOpenConfigurationPanel,
            'data-testid': 'quick-action-configuration',
        },
    ], [
        t,
        handleOpenBeneficiariesModal,
        handleOpenContractedBenefitsModal,
        handleOpenTrackingCardsModal,
        handleOpenAddressListModal,
        handleOpenConfigurationPanel,
        handleNavigateToNetwork,
        isMultipleAddressesEnabled,
    ]);
    return (_jsxs("div", { className: styles.container, children: [repeatRechargeErrorVisible && (_jsx(Alert, { message: t('benefits.home.repeat-order-error'), appearance: 'error', alertType: 'toast', density: 'compact', duration: 8, alignItems: 'center', hideCloseButton: false, className: styles.repeatRechargeErrorAlert, testIdIdentifier: 'HomePageRepeatRechargeErrorAlert', onClose: () => setRepeatRechargeErrorVisible(false) })), _jsx("div", { className: styles.headerSection, children: _jsx("h1", { className: styles.pageTitle, children: t('benefits.home.title') }) }), _jsx("div", { "data-benefits-onboarding-anchor": 'products', children: _jsxs(Card, { className: styles.productsCard, children: [_jsxs("div", { className: styles.cardHeader, children: [_jsx("h2", { className: styles.cardTitle, children: t('benefits.home.products.title') }), newRechargeButton] }), _jsx("div", { className: styles.benefitCardsGrid, children: isLoading ? (_jsx(Loading, {})) : (_jsx(BenefitCards, { cards: getActiveContractProducts(products), "data-testid": 'benefits-status-cards', onConfigure: handleConfigure })) })] }) }), _jsxs("div", { className: styles.bottomSection, "data-benefits-onboarding-anchor": 'bottom-workspace', children: [_jsx("div", { "data-benefits-onboarding-anchor": 'recharges', className: styles.rechargesMainColumn, children: _jsxs(Card, { className: styles.tabsCard, children: [_jsx("div", { className: styles.tabHeader, children: _jsx("div", { className: styles.tabNavigation, children: _jsx("button", { onClick: () => setActiveTab(HomePageTab.RECHARGES), className: `${styles.tabButton} ${activeTab === HomePageTab.RECHARGES ? styles.tabButtonActive : styles.tabButtonInactive}`, children: t('benefits.home.tabs.recharges') }) }) }), _jsx("div", { className: styles.tabContent, children: activeTab === HomePageTab.RECHARGES && (_jsx(RechargeHistoryContainer, { contractId: contractData.id, onRechargeClick: handleRechargeClick, "data-testid": 'HomeRechargeHistory' })) })] }) }), _jsxs("div", { className: styles.tabsNavigation, children: [_jsx(HomeSidebarBanners, { onHappyHolidaysEndOfTheYearRechargeBannerClick: handleHappyHolidaysEndOfTheYearRechargeBannerClick, appDomain: appDomain, "data-testid": 'HomePageSideBanners' }), _jsx(QuickActions, { actions: quickActions }), _jsx(ProductServiceSurveyAlert, { appDomain: appDomain, "data-testid": 'HomePageSurveyAlert' })] })] }), _jsx(WelcomeModal, { ...welcomeModalProps, "data-testid": 'benefits-welcome-modal' }), _jsx(RechargeDetailsModal, { isOpen: isModalOpen, rechargeId: selectedRecharge?.id || null, contractId: contractData.id, onClose: handleCloseModal, "data-testid": 'RechargeDetailsModal' }), _jsx(BeneficiariesPage, { isOpen: isBeneficiariesModalOpen, contractId: contractData.id, onClose: handleCloseBeneficiariesModal, "data-testid": 'BeneficiariesModal' }), _jsx(ContractedBenefitsModal, { show: isContractedBenefitsModalOpen, contractId: contractData.id, onHide: handleCloseContractedBenefitsModal, onContractNewBenefit: handleContractNewBenefit, "data-testid": 'ContractedBenefitsModal' }), _jsx(TrackingCardsModal, { isOpen: isTrackingCardsModalOpen, contractId: contractData.id, onClose: handleCloseTrackingCardsModal, "data-testid": 'TrackingCardsModal' }), _jsx(AddressListModal, { show: isAddressListModalOpen, onHide: handleCloseAddressListModal, title: t('deliveryAddress'), testIdIdentifier: 'HomePageAddressListModal' }), _jsx(NetworkPage, { isOpen: isNetworkModalOpen, onClose: handleCloseNetworkModal, "data-testid": 'NetworkModal' }), _jsx(ConfigurationPanel, { show: isConfigurationPanelOpen, contractId: contractData.id, onHide: handleCloseConfigurationPanel, testIdIdentifier: 'HomePageConfigurationPanel' }), _jsx(HappyHolidaysEndOfTheYearRechargeModal, { isOpen: isHappyHolidaysEndOfTheYearRechargeModalOpen, onClose: handleCloseHappyHolidaysEndOfTheYearRechargeModal, "data-testid": 'HappyHolidaysEndOfTheYearRechargeModal' }), benefitsOnboardingWizard && (_jsx(BenefitsOnboardingWizard, { active: true, startPhase: benefitsOnboardingWizard.mode === 'tour' ? 'tour' : 'skipConfirm', onReopenFromSkipCancel: benefitsOnboardingWizard.onReopenFromSkipCancel, onFlowFinished: benefitsOnboardingWizard.onFlowFinished }, `${contractData.id}-onboarding-${benefitsOnboardingWizard.mode}`))] }));
}
export const HomePage = props => {
    return (_jsx(TranslationProvider, { translations: beneficiosTranslations, defaultLanguage: 'pt-BR', children: _jsx(HomePageContent, { ...props }) }));
};
